import { useCallback } from 'react';
import { toJS } from 'mobx';
import { nanoid } from 'nanoid';
import { buildMessageUploadFolder, isEmpty, isEmptyMessage } from '../../../../helpers/common';
import { CommentFilesInterface } from '../../../../interfaces/TaskInterface';
import { CHAT_GROUP_TYPE, DRAFT_MESSAGE_TYPE } from '../../../../global/constants';
import { sendDiscussionMessage, updateDiscussionMessageDetail } from '../../../../services/chatMessageService';
import { syncConversation } from '../../../../services/chatMessageSyncService';
import { captureException } from '../../../../services/logService';
import { trackAnalyticActivity } from '../../../../services/analyticsService';
import DraftMessageCacheService from '../../../../services/draftMessageCacheService';
import { MESSAGE_ANALYTICS } from '../../../../global/analyticsConstants';
import { ChatGroupInterface, MessageDetailsInterface } from '../../../../interfaces/chatMessageInterface';
import { ReplyMessageDataInterface } from '../../../../interfaces/MessageInterface';
import { TiptapEditorRef } from '../../../../interfaces/tiptapInterface';
import { RootStore } from '../../../../mobx/rootStore';
import { UserModel } from '../../../../mobx/models/user';
import { WorkspaceModel } from '../../../../mobx/models/workspace';
import { ProjectModel } from '../../../../mobx/models/project';
import { processFileLinksInContent } from './useFileProcessing';
import { useMessagePayloadBuilder } from './useMessagePayloadBuilder';

interface UseSendMessageParams {
  content: string;
  editingMessage: MessageDetailsInterface | null;
  replyMessage: ReplyMessageDataInterface;
  currentProject: ProjectModel | ChatGroupInterface | null;
  isNavbarMessage: boolean;
  currentUser: UserModel | null;
  workspace: WorkspaceModel | null;
  setDisplayedMessages: (updater: (prev: MessageDetailsInterface[]) => MessageDetailsInterface[]) => void;
  setReplyMessage: (value: ReplyMessageDataInterface) => void;
  setEditingMessage: (value: MessageDetailsInterface | null) => void;
  setAttachment: (value: boolean) => void;
  clearDraft: () => Promise<void>;
  inlineRef: React.MutableRefObject<TiptapEditorRef | null>;
  Cancel: () => void;
  rootStore: RootStore;
}

interface UseSendMessageReturn {
  onSendComment: (filesArg: CommentFilesInterface[], isEdit?: boolean) => Promise<void>;
  loading: boolean;
}

export const useSendMessage = ({
  content,
  editingMessage,
  replyMessage,
  currentProject,
  isNavbarMessage,
  currentUser,
  workspace,
  setDisplayedMessages,
  setReplyMessage,
  setEditingMessage,
  setAttachment,
  clearDraft,
  inlineRef,
  Cancel,
  rootStore
}: UseSendMessageParams): UseSendMessageReturn => {
  const loading = rootStore.richTextStore.isLoading;

  const {
    buildSendPayload,
    buildUpdatePayload,
    buildOptimisticMessage,
    buildNavbarOptimisticMessage,
    buildReplyPayload
  } = useMessagePayloadBuilder();

  const resetEditorState = useCallback(async (): Promise<void> => {
    const editorElement: HTMLElement | null = document.getElementById('inlineRTE_rte-edit-view');
    if (editorElement) {
      editorElement.innerHTML = '<p><br /></p>';
    }
    setReplyMessage({});
    rootStore.richTextStore.reset();
    inlineRef.current?.clearContent();
    setAttachment(false);
    await clearDraft();
  }, [setReplyMessage, rootStore.richTextStore, inlineRef, setAttachment, clearDraft]);

  const sanitizeCommentContent = useCallback((rawContent: string): string => {
    let sanitizedContent = rawContent;
    const pattern = /<p><span>​<\/span><\/p>|<p><br><\/p>|<p><\/p>|<p><br><br><\/p>/g;
    if (!isEmpty(rawContent)) {
      while (pattern.test(sanitizedContent)) {
        sanitizedContent = sanitizedContent?.replace(pattern, '');
      }
      if (sanitizedContent.match(/<br\s*>\s*<\/p>$/)) {
        sanitizedContent = sanitizedContent?.replace(/<br\s*>\s*<\/p>$/, '</p>');
      }
    }
    return sanitizedContent;
  }, []);

  const handleEditMessage = useCallback(
    async (uploadedFilesData: CommentFilesInterface[], commentData: string): Promise<void> => {
      if (!editingMessage) return;

      const replyPayload = buildReplyPayload(replyMessage);
      const updatedHtmlString = commentData;

      setDisplayedMessages((prev) =>
        prev.map((message) =>
          message.id === editingMessage.id
            ? {
                ...message,
                message: updatedHtmlString,
                uploadedFile: uploadedFilesData,
                reply: replyPayload
              }
            : message
        )
      );

      if ((currentProject as ChatGroupInterface)?.messages && currentProject?.id) {
        rootStore.chatMessageStore.updateCurrentChatGroupMessages(currentProject.id, (current) =>
          current.map((message: MessageDetailsInterface) =>
            message.id === editingMessage.id
              ? {
                  ...message,
                  message: updatedHtmlString,
                  uploadedFile: uploadedFilesData,
                  reply: replyPayload
                }
              : message
          )
        );
      }

      const updatePayload = buildUpdatePayload({
        updatedHtmlString,
        currentProject,
        isNavbarMessage,
        uploadedFilesData
      });

      const updated = await updateDiscussionMessageDetail(editingMessage.id, updatePayload);
      // Sync the sidebar row and cache (the backend signals other members, not the editor).
      if (updated && currentProject?.id) {
        syncConversation(
          currentProject.id,
          isNavbarMessage
            ? (currentProject as ChatGroupInterface)?.type ?? CHAT_GROUP_TYPE.PERSONAL
            : CHAT_GROUP_TYPE.PROJECT
        );
      }

      await resetEditorState();
      setEditingMessage(null);
      Cancel();
    },
    [
      editingMessage,
      replyMessage,
      currentProject,
      isNavbarMessage,
      buildReplyPayload,
      buildUpdatePayload,
      setDisplayedMessages,
      rootStore.chatMessageStore,
      resetEditorState,
      setEditingMessage,
      Cancel
    ]
  );

  const handleNewMessage = useCallback(
    async (uploadedFilesData: CommentFilesInterface[], commentData: string, nanoId: string): Promise<void> => {
      const draftMessage = {
        id: currentProject?.id,
        type: DRAFT_MESSAGE_TYPE.COMMENT,
        message: commentData,
        file: uploadedFilesData || []
      };

      if (isNavbarMessage) {
        const navbarOptimisticMessage = buildNavbarOptimisticMessage({
          commentData,
          currentProject,
          currentUser,
          uploadedFilesData,
          replyMessage,
          nanoId
        });
        if (currentProject?.id) {
          rootStore.chatMessageStore.updateCurrentChatGroupMessages(currentProject.id, (current) => [
            ...current,
            navbarOptimisticMessage as unknown as MessageDetailsInterface
          ]);
        }
      } else {
        const optimisticMessage = buildOptimisticMessage({
          commentData,
          currentProject,
          currentUser,
          uploadedFilesData,
          replyMessage,
          nanoId
        });
        rootStore.projectStore.updateDiscussionData((current) => [
          ...current,
          optimisticMessage as unknown as MessageDetailsInterface
        ]);
      }

      await resetEditorState();

      const processedCommentData = await processFileLinksInContent(
        commentData,
        buildMessageUploadFolder(currentProject?.id)
      );

      const sendPayload = buildSendPayload({
        commentData: processedCommentData,
        currentProject,
        isNavbarMessage,
        currentUser,
        workspace,
        uploadedFilesData,
        replyMessage,
        nanoId
      });

      const result = await sendDiscussionMessage(sendPayload as Parameters<typeof sendDiscussionMessage>[0]);

      if (result) {
        trackAnalyticActivity(MESSAGE_ANALYTICS.SENT, {
          type: isNavbarMessage
            ? (currentProject as ChatGroupInterface)?.type === CHAT_GROUP_TYPE.PERSONAL
              ? 'personal'
              : 'project'
            : 'project',
          with_reply: isEmpty(replyMessage) ? false : true
        });
        if (currentProject?.id) {
          syncConversation(
            currentProject.id,
            isNavbarMessage
              ? (currentProject as ChatGroupInterface)?.type ?? CHAT_GROUP_TYPE.PERSONAL
              : CHAT_GROUP_TYPE.PROJECT
          );
        }
      } else {
        await DraftMessageCacheService.getInstance()?.addBulk([draftMessage]);
      }
    },
    [
      currentProject,
      isNavbarMessage,
      currentUser,
      workspace,
      replyMessage,
      buildNavbarOptimisticMessage,
      buildOptimisticMessage,
      buildSendPayload,
      resetEditorState,
      rootStore.chatMessageStore,
      rootStore.projectStore
    ]
  );

  const onSendComment = useCallback(
    async (filesArg: CommentFilesInterface[], isEdit?: boolean): Promise<void> => {
      const uploadedFilesData = toJS(filesArg);
      try {
        if (isEmptyMessage(content) && isEmpty(uploadedFilesData)) {
          return rootStore.alertNotificationStore.setErrorMessage("Message can't be empty.");
        }

        if (!isEmpty(content) || uploadedFilesData?.length > 0) {
          if (isNavbarMessage && isEmpty(currentProject?.id)) {
            return rootStore.alertNotificationStore.setErrorMessage('Please select a recipient user.');
          }

          rootStore.richTextStore.setLoading(true);
          const nanoId = nanoid();
          const commentData = sanitizeCommentContent(content);

          if (isEdit && editingMessage) {
            await handleEditMessage(uploadedFilesData, commentData);
          } else {
            await handleNewMessage(uploadedFilesData, commentData, nanoId);
          }
        }
      } catch (error) {
        captureException(error);
      } finally {
        rootStore.richTextStore.setLoading(false);
      }
    },
    [
      content,
      isNavbarMessage,
      currentProject,
      editingMessage,
      sanitizeCommentContent,
      handleEditMessage,
      handleNewMessage,
      rootStore.richTextStore,
      rootStore.alertNotificationStore
    ]
  );

  return {
    onSendComment,
    loading
  };
};
